import React, { useEffect, useState } from 'react';
import {
  MessageCircle,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Users,
  Clock,
  Shield,
  Check,
  Star,
  ArrowRight
} from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    // Google Analytics
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-V8ESDMZ5NZ');

    // SEO-OPTIMIZED: Title and meta description
    document.title = 'Frequently Asked Questions | TicketDesk';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Common questions about TicketDesk pricing, features, integrations, free trial, and customer support software. Get answers about Gmail ticketing, review monitoring, and team collaboration.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://www.ticketdesk.app/faq';

    // FAQ Structured Data
    const faqStructuredData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does TicketDesk cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "TicketDesk offers a free plan with 10 tickets per month. Paid plans start at $29/month for 200 tickets (Starter), $79/month for 1,000 tickets (Growth), and $199/month for 5,000 tickets (Scale). All plans include unlimited team members, multi-channel support (Gmail, Facebook, WhatsApp), and Trustpilot review monitoring. No per-agent pricing—one flat rate regardless of team size."
          }
        },
        {
          "@type": "Question",
          "name": "What integrations does TicketDesk support?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "TicketDesk integrates with Gmail for email ticketing, Facebook Messenger for social media support, WhatsApp Business for messaging, and website chat widgets. For review monitoring, TicketDesk connects to Trustpilot, Capterra, G2, and Google Reviews. All channels unify into one inbox where every message becomes a trackable ticket your team can manage together."
          }
        },
        {
          "@type": "Question",
          "name": "Can I add unlimited team members?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, all TicketDesk plans include unlimited team members at no extra cost. Unlike competitors that charge per agent, TicketDesk uses ticket-volume pricing. Add your entire team—full-time staff, part-timers, seasonal help—without increasing your monthly bill. Perfect for small businesses where multiple people occasionally handle customer support without dedicated agent roles."
          }
        },
        {
          "@type": "Question",
          "name": "How long does setup take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "TicketDesk setup takes approximately 60 seconds. Connect your Gmail account with one click using secure OAuth authentication, and you're immediately ready to handle customer support tickets. Adding Facebook Messenger or WhatsApp takes another minute each. No complex configuration, training videos, or IT involvement required. Start managing customer inquiries within minutes, not days like enterprise software."
          }
        },
        {
          "@type": "Question",
          "name": "Is there a free trial?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, TicketDesk offers a 7-day free trial of all paid plan features. No credit card required to start your trial. Test unlimited team members, multi-channel support, AI reply polishing, and review monitoring risk-free. After the trial, you can choose a paid plan or downgrade to the free plan (10 tickets/month). Cancel anytime during the trial with no charges."
          }
        },
        {
          "@type": "Question",
          "name": "What's included in the free plan?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The TicketDesk free plan includes 10 tickets per month, unlimited team members, Gmail integration, basic ticket assignment and tracking, and access to the unified inbox. Perfect for micro businesses, solopreneurs testing the platform, or very low-volume support needs. Upgrade to paid plans when you need more ticket capacity, additional channels (Facebook, WhatsApp), or review monitoring features."
          }
        },
        {
          "@type": "Question",
          "name": "How does TicketDesk compare to Zendesk?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "TicketDesk costs 90% less than Zendesk for small teams. Zendesk charges $55+ per agent per month with per-seat pricing. TicketDesk starts at $29/month total with unlimited team members. TicketDesk focuses on essentials small businesses actually use—Gmail ticketing, Facebook/WhatsApp support, review monitoring—without enterprise complexity. Setup takes 60 seconds vs. days of Zendesk configuration. Built specifically for small businesses, not enterprises."
          }
        },
        {
          "@type": "Question",
          "name": "Can I use TicketDesk for e-commerce customer support?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, TicketDesk is ideal for e-commerce shops. Handle order questions via Gmail, shipping inquiries through Facebook Messenger, and return requests on WhatsApp—all in one inbox. Trustpilot review monitoring alerts you instantly to negative reviews so you can respond before they hurt sales. Perfect for Shopify, WooCommerce, or any online store needing multi-channel support without Zendesk pricing."
          }
        },
        {
          "@type": "Question",
          "name": "What are Trustpilot review alerts?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Trustpilot review alerts notify you immediately when new reviews appear on your Trustpilot business profile, with special priority for reviews under 3 stars. TicketDesk automatically checks your listings and creates support tickets for negative reviews requiring responses. Respond within hours instead of days to protect your reputation. Also supports Capterra, G2, and Google Reviews monitoring across all paid plans."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need a credit card for the free trial?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No, TicketDesk does not require a credit card to start your 7-day free trial. Simply sign up with your email, connect Gmail, and start handling support tickets immediately. At the end of the trial, choose a paid plan if you need more capacity, or continue using the free plan (10 tickets/month) indefinitely. No automatic charges, no payment information required upfront."
          }
        },
        {
          "@type": "Question",
          "name": "Can I cancel anytime?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, you can cancel your TicketDesk subscription anytime with no penalties or cancellation fees. Subscriptions are month-to-month with no long-term contracts required. Cancel from your account settings, and you'll retain access through the end of your current billing period. Downgrade to the free plan (10 tickets/month) instead of canceling if you want to keep basic access without paying."
          }
        },
        {
          "@type": "Question",
          "name": "Is TicketDesk suitable for SaaS companies?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, SaaS companies use TicketDesk for customer support, bug reports, and feature requests. Unified inbox consolidates Gmail support emails, Facebook inquiries, and website chat into trackable tickets. Assign technical issues to developers, billing questions to finance, and onboarding help to customer success—all from one platform. G2 and Capterra review monitoring helps SaaS companies respond quickly to software review feedback."
          }
        }
      ]
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(faqStructuredData);
  }, []);

  const faqs = [
    {
      icon: DollarSign,
      question: "How much does TicketDesk cost?",
      answer: "TicketDesk offers a free plan with 10 tickets per month. Paid plans start at $29/month for 200 tickets (Starter), $79/month for 1,000 tickets (Growth), and $199/month for 5,000 tickets (Scale). All plans include unlimited team members, multi-channel support (Gmail, Facebook, WhatsApp), and Trustpilot review monitoring. No per-agent pricing—one flat rate regardless of team size."
    },
    {
      icon: MessageCircle,
      question: "What integrations does TicketDesk support?",
      answer: "TicketDesk integrates with Gmail for email ticketing, Facebook Messenger for social media support, WhatsApp Business for messaging, and website chat widgets. For review monitoring, TicketDesk connects to Trustpilot, Capterra, G2, and Google Reviews. All channels unify into one inbox where every message becomes a trackable ticket your team can manage together."
    },
    {
      icon: Users,
      question: "Can I add unlimited team members?",
      answer: "Yes, all TicketDesk plans include unlimited team members at no extra cost. Unlike competitors that charge per agent, TicketDesk uses ticket-volume pricing. Add your entire team—full-time staff, part-timers, seasonal help—without increasing your monthly bill. Perfect for small businesses where multiple people occasionally handle customer support without dedicated agent roles."
    },
    {
      icon: Clock,
      question: "How long does setup take?",
      answer: "TicketDesk setup takes approximately 60 seconds. Connect your Gmail account with one click using secure OAuth authentication, and you're immediately ready to handle customer support tickets. Adding Facebook Messenger or WhatsApp takes another minute each. No complex configuration, training videos, or IT involvement required. Start managing customer inquiries within minutes, not days like enterprise software."
    },
    {
      icon: Check,
      question: "Is there a free trial?",
      answer: "Yes, TicketDesk offers a 7-day free trial of all paid plan features. No credit card required to start your trial. Test unlimited team members, multi-channel support, AI reply polishing, and review monitoring risk-free. After the trial, you can choose a paid plan or downgrade to the free plan (10 tickets/month). Cancel anytime during the trial with no charges."
    },
    {
      icon: Shield,
      question: "What's included in the free plan?",
      answer: "The TicketDesk free plan includes 10 tickets per month, unlimited team members, Gmail integration, basic ticket assignment and tracking, and access to the unified inbox. Perfect for micro businesses, solopreneurs testing the platform, or very low-volume support needs. Upgrade to paid plans when you need more ticket capacity, additional channels (Facebook, WhatsApp), or review monitoring features."
    },
    {
      icon: DollarSign,
      question: "How does TicketDesk compare to Zendesk?",
      answer: "TicketDesk costs 90% less than Zendesk for small teams. Zendesk charges $55+ per agent per month with per-seat pricing. TicketDesk starts at $29/month total with unlimited team members. TicketDesk focuses on essentials small businesses actually use—Gmail ticketing, Facebook/WhatsApp support, review monitoring—without enterprise complexity. Setup takes 60 seconds vs. days of Zendesk configuration. Built specifically for small businesses, not enterprises."
    },
    {
      icon: MessageCircle,
      question: "Can I use TicketDesk for e-commerce customer support?",
      answer: "Yes, TicketDesk is ideal for e-commerce shops. Handle order questions via Gmail, shipping inquiries through Facebook Messenger, and return requests on WhatsApp—all in one inbox. Trustpilot review monitoring alerts you instantly to negative reviews so you can respond before they hurt sales. Perfect for Shopify, WooCommerce, or any online store needing multi-channel support without Zendesk pricing."
    },
    {
      icon: Star,
      question: "What are Trustpilot review alerts?",
      answer: "Trustpilot review alerts notify you immediately when new reviews appear on your Trustpilot business profile, with special priority for reviews under 3 stars. TicketDesk automatically checks your listings and creates support tickets for negative reviews requiring responses. Respond within hours instead of days to protect your reputation. Also supports Capterra, G2, and Google Reviews monitoring across all paid plans."
    },
    {
      icon: Shield,
      question: "Do I need a credit card for the free trial?",
      answer: "No, TicketDesk does not require a credit card to start your 7-day free trial. Simply sign up with your email, connect Gmail, and start handling support tickets immediately. At the end of the trial, choose a paid plan if you need more capacity, or continue using the free plan (10 tickets/month) indefinitely. No automatic charges, no payment information required upfront."
    },
    {
      icon: Check,
      question: "Can I cancel anytime?",
      answer: "Yes, you can cancel your TicketDesk subscription anytime with no penalties or cancellation fees. Subscriptions are month-to-month with no long-term contracts required. Cancel from your account settings, and you'll retain access through the end of your current billing period. Downgrade to the free plan (10 tickets/month) instead of canceling if you want to keep basic access without paying."
    },
    {
      icon: Users,
      question: "Is TicketDesk suitable for SaaS companies?",
      answer: "Yes, SaaS companies use TicketDesk for customer support, bug reports, and feature requests. Unified inbox consolidates Gmail support emails, Facebook inquiries, and website chat into trackable tickets. Assign technical issues to developers, billing questions to finance, and onboarding help to customer success—all from one platform. G2 and Capterra review monitoring helps SaaS companies respond quickly to software review feedback."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const trackEvent = (eventName, eventParams = {}) => {
    if (window.gtag) {
      window.gtag('event', eventName, eventParams);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle">
                  <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                </svg>
              </div>
              <span className="ml-2 sm:ml-3 text-lg sm:text-xl font-semibold text-gray-900">TicketDesk</span>
            </a>
            <div className="flex items-center space-x-2 sm:space-x-4 lg:space-x-8">
              <a href="/blog" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden md:block">Blog</a>
              <a href="/#features" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden md:block">Features</a>
              <a href="/#pricing" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900">Pricing</a>
              <a href="/login" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden lg:block">Sign In</a>
              <a href="/register" className="bg-purple-600 text-white px-3 sm:px-4 lg:px-5 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-purple-700 whitespace-nowrap">
                Try free
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              Frequently Asked Questions
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 leading-relaxed">
              Everything you need to know about TicketDesk pricing, features, integrations, and getting started with customer support software.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const Icon = faq.icon;
              const isOpen = openIndex === index;

              return (
                <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center flex-1">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                        <Icon className="w-5 h-5 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                    </div>
                    {isOpen ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-6 pb-5 pl-20">
                      <p className="text-gray-700 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-purple-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl text-white mb-8 opacity-90">
            Start your free trial and experience TicketDesk firsthand. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/register"
              onClick={() => trackEvent('cta_click', { location: 'faq_bottom' })}
              className="inline-flex items-center justify-center bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <a
              href="https://calendly.com/hello-ticketdesk/demo"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors"
            >
              Book a Demo
            </a>
          </div>
          <p className="text-white mt-6 opacity-75 text-sm">7-day free trial • No credit card required • Cancel anytime</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold">TicketDesk</span>
              </div>
              <p className="text-gray-400 max-w-md">
                Customer support software for small businesses. Unify Gmail, Facebook, WhatsApp, and review monitoring in one simple inbox.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/#features" className="hover:text-white">Features</a></li>
                <li><a href="/#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="/faq" className="hover:text-white">FAQ</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Resources</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/blog" className="hover:text-white">Blog</a></li>
                <li><a href="/compare/zendesk" className="hover:text-white">vs Zendesk</a></li>
                <li><a href="/for-ecommerce" className="hover:text-white">For E-commerce</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 TicketDesk. Customer support software for small businesses.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
